/**
 * Senior Review Workflow Page
 * 
 * Enhanced with:
 * - RBAC gating (admin/admin_tech only)
 * - Review statistics dashboard
 * - Enhanced filtering (by review type, assignment status, revision status)
 * - Bulk actions (assign multiple, approve multiple)
 * - Individual proposal review dashboard integration
 * - Full-width proposal list
 * - Quick status change buttons
 * - Share functionality
 * - Clickable proposals with navigation
 * - Edge case handling
 * - Dynamic filter dropdowns
 * - Fixed date input overflow
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, Input, Select, usePermissions } from '@tracekit/core';
import { 
  listReports, 
  updateReportStatus,
  useWorkspace,
  useAuthContext,
  useToastStore,
  type Report,
} from '@tracekit/core';
import { ReviewDashboard } from '@tracekit/core/components/Review';
import { 
  getReviewAssignments,
  getRevisionRequests,
  getSubmissionStatus,
  assignReviewer,
  completeReview,
  getAvailableReviewers,
  type ReviewAssignment,
  type RevisionRequest,
  type ReviewType,
  type AvailableReviewer,
} from '@tracekit/core/api/reviewApi';
import { Filter, X, Shield, AlertTriangle, FileText, CheckCircle, Users, Clock, TrendingUp, BarChart3, CheckSquare, UserPlus } from 'lucide-react';
import { AggregatedReviewList } from '../components/AggregatedReviewList';
import { ReviewFilters } from '../types/review';

interface ReviewStatistics {
  totalProposals: number;
  submittedForReview: number;
  inReview: number;
  approved: number;
  rejected: number;
  pendingAssignments: number;
  activeAssignments: number;
  pendingRevisions: number;
  criticalRevisions: number;
}

export function ReviewWorkflowPage() {
  const { projectId } = useWorkspace();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const { hasPermission, hasAnyPermission, isLoading: isLoadingPermissions } = usePermissions();
  
  const [filters, setFilters] = useState<ReviewFilters & {
    reviewType?: string;
    assignmentStatus?: string;
    revisionStatus?: string;
  }>({
    status: 'all',
    clientName: '',
    dateRange: undefined,
    reviewer: '',
    reviewType: 'all',
    assignmentStatus: 'all',
    revisionStatus: 'all',
  });
  const [availableClientNames, setAvailableClientNames] = useState<string[]>([]);
  const [availableReviewers, setAvailableReviewers] = useState<string[]>([]);
  const [selectedProposals, setSelectedProposals] = useState<Set<string>>(new Set());
  const [selectedProposal, setSelectedProposal] = useState<string | null>(null);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkReviewer, setBulkReviewer] = useState('');
  const [bulkReviewType, setBulkReviewType] = useState<ReviewType>('full');
  const [bulkDeadline, setBulkDeadline] = useState('');

  // RBAC: Only admin or admin_tech can access
  const canAccessReview = useMemo(() => {
    if (isLoadingPermissions) return false;
    return hasAnyPermission([
      'keystone_rfp_admin_tech',
      'admin_tech',
      'keystone_proposal_admin_tech',
      'admin',
      '*', // Superadmin
    ]);
  }, [hasAnyPermission, isLoadingPermissions]);

  // Determine if user is a reviewer (has review permission but not admin_tech)
  const isReviewer = useMemo(() => {
    if (isLoadingPermissions) return false;
    // If user has admin_tech permissions, they're not a reviewer (they're an admin)
    if (canAccessReview) return false;
    // Otherwise, check if they have review permission
    return hasPermission('review') || hasPermission('keystone_rfp_review');
  }, [hasPermission, canAccessReview, isLoadingPermissions]);

  const { data: reports, isLoading, error } = useQuery({
    queryKey: ['reports', projectId],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }
      const reportsList = await listReports(projectId);
      return reportsList;
    },
    enabled: !!projectId && canAccessReview,
    refetchInterval: 30000, // Refresh every 30s
  });

  // Fetch review data for all proposals to calculate statistics
  const rfpProposals = useMemo(() => {
    if (!reports) return [];
    return reports.filter((r: Report) => {
      const metadata = r.metadata || {};
      return r.report_type === 'rfp_proposal' ||
        (r.report_type === 'custom' &&
         (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));
    });
  }, [reports]);

  // Calculate review statistics
  const statistics = useMemo<ReviewStatistics>(() => {
    const stats: ReviewStatistics = {
      totalProposals: rfpProposals.length,
      submittedForReview: 0,
      inReview: 0,
      approved: 0,
      rejected: 0,
      pendingAssignments: 0,
      activeAssignments: 0,
      pendingRevisions: 0,
      criticalRevisions: 0,
    };

    // This would ideally come from aggregated API calls, but for now we'll calculate from reports
    rfpProposals.forEach((r: Report) => {
      const reviewStatus = r.status === 'completed' ? 'draft' : r.status;
      if (reviewStatus === 'submitted') stats.submittedForReview++;
      if (reviewStatus === 'in_review') stats.inReview++;
      if (reviewStatus === 'approved') stats.approved++;
      if (reviewStatus === 'rejected') stats.rejected++;
    });

    return stats;
  }, [rfpProposals]);

  // Extract unique client names and reviewers from reports for dropdowns
  const { uniqueClients, uniqueReviewers } = useMemo(() => {
    if (!reports) return { uniqueClients: [], uniqueReviewers: [] };

    const clients = new Set<string>();
    const reviewers = new Set<string>();

    reports.forEach((r: Report) => {
      const metadata = r.metadata || {};
      const isRfpProposal =
        r.report_type === 'rfp_proposal' ||
        (r.report_type === 'custom' &&
         (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));
      
      if (isRfpProposal) {
        const clientName = metadata.client_name || r.client_name;
        if (clientName) {
          clients.add(clientName);
        }
        
        const reviewer = metadata.current_reviewer || metadata.reviewer;
        if (reviewer) {
          reviewers.add(reviewer);
        }
      }
    });

    return {
      uniqueClients: Array.from(clients).sort(),
      uniqueReviewers: Array.from(reviewers).sort(),
    };
  }, [reports]);

  const updateStatusMutation = useMutation({
    mutationFn: ({ reportId, status }: { reportId: string; status: string }) =>
      updateReportStatus(reportId, status as any),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
      queryClient.invalidateQueries({ queryKey: ['aggregated-reviews', projectId] });
      addToast({
        message: `Proposal status updated to ${variables.status}`,
        type: 'success',
      });
    },
    onError: (error: any) => {
      addToast({
        message: error.message || 'Failed to update proposal status',
        type: 'error',
      });
    },
  });

  // Bulk assign reviewers mutation
  const bulkAssignMutation = useMutation({
    mutationFn: async ({ reportIds, reviewerId, reviewType, deadline }: {
      reportIds: string[];
      reviewerId: string;
      reviewType: ReviewType;
      deadline?: string;
    }) => {
      const results = await Promise.allSettled(
        reportIds.map(reportId => 
          assignReviewer(reportId, reviewerId, reviewType, deadline)
        )
      );
      return results;
    },
    onSuccess: (results) => {
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
      queryClient.invalidateQueries({ queryKey: ['aggregated-reviews', projectId] });
      queryClient.invalidateQueries({ queryKey: ['review-assignments'] });
      setSelectedProposals(new Set());
      setShowBulkAssign(false);
      addToast({
        message: `Assigned reviewer to ${succeeded} proposal(s)${failed > 0 ? `, ${failed} failed` : ''}`,
        type: succeeded > 0 ? 'success' : 'error',
      });
    },
    onError: (error: any) => {
      addToast({
        message: error.message || 'Failed to assign reviewers',
        type: 'error',
      });
    },
  });

  // Bulk approve mutation
  const bulkApproveMutation = useMutation({
    mutationFn: async ({ reportIds }: { reportIds: string[] }) => {
      // Get assignments for each report and approve them
      const results = await Promise.allSettled(
        reportIds.map(async (reportId) => {
          const assignments = await getReviewAssignments(reportId);
          const activeAssignment = assignments.find(a => a.status === 'in_progress' || a.status === 'pending');
          if (activeAssignment) {
            return completeReview(reportId, activeAssignment.id, 'approved', 'Bulk approved');
          }
          throw new Error(`No active assignment found for report ${reportId}`);
        })
      );
      return results;
    },
    onSuccess: (results) => {
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
      queryClient.invalidateQueries({ queryKey: ['aggregated-reviews', projectId] });
      queryClient.invalidateQueries({ queryKey: ['review-assignments'] });
      setSelectedProposals(new Set());
      addToast({
        message: `Approved ${succeeded} proposal(s)${failed > 0 ? `, ${failed} failed` : ''}`,
        type: succeeded > 0 ? 'success' : 'error',
      });
    },
    onError: (error: any) => {
      addToast({
        message: error.message || 'Failed to approve proposals',
        type: 'error',
      });
    },
  });

  const handleStatusChange = (reportId: string, newStatus: string) => {
    updateStatusMutation.mutate({ reportId, status: newStatus });
  };

  const handleProposalClick = (reportId: string) => {
    setSelectedProposal(reportId);
  };

  const handleBulkAssign = () => {
    if (!bulkReviewer) {
      addToast({ message: 'Please select a reviewer', type: 'error' });
      return;
    }
    if (selectedProposals.size === 0) {
      addToast({ message: 'Please select at least one proposal', type: 'error' });
      return;
    }
    bulkAssignMutation.mutate({
      reportIds: Array.from(selectedProposals),
      reviewerId: bulkReviewer,
      reviewType: bulkReviewType,
      deadline: bulkDeadline ? new Date(bulkDeadline).toISOString() : undefined,
    });
  };

  const handleBulkApprove = () => {
    if (selectedProposals.size === 0) {
      addToast({ message: 'Please select at least one proposal', type: 'error' });
      return;
    }
    if (confirm(`Approve ${selectedProposals.size} selected proposal(s)?`)) {
      bulkApproveMutation.mutate({ reportIds: Array.from(selectedProposals) });
    }
  };

  // Loading permissions
  if (isLoadingPermissions) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Checking permissions...</span>
        </div>
      </div>
    );
  }

  // Access denied
  if (!canAccessReview) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-6">
          <div className="text-center py-12">
            <Shield className="h-16 w-16 mx-auto text-red-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You do not have permission to access the Review Workflow.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              This feature requires Admin or Admin Tech role.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Loading reports
  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
          <span className="ml-3 text-gray-600">Loading proposals...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 mx-auto text-red-400 mb-4" />
            <p className="text-red-600 dark:text-red-400 mb-4">Failed to load proposals</p>
            <Button 
              onClick={() => queryClient.invalidateQueries({ queryKey: ['reports', projectId] })}
              variant="outline"
            >
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // No project selected
  if (!projectId) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-6">
          <div className="text-center py-12">
            <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">Please select a project to view proposals for review</p>
          </div>
        </Card>
      </div>
    );
  }

  // Show individual proposal review dashboard if selected
  if (selectedProposal) {
    return (
      <div className="container mx-auto p-4 md:p-6">
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => setSelectedProposal(null)}
          >
            ← Back to Review Workflow
          </Button>
        </div>
        <ReviewDashboard 
          reportId={selectedProposal}
          onStatusChange={() => {
            queryClient.invalidateQueries({ queryKey: ['reports', projectId] });
            queryClient.invalidateQueries({ queryKey: ['aggregated-reviews', projectId] });
          }}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Review Workflow</h1>
            <p className="text-gray-600 mt-1">
              Review and approve proposals (Admin/Admin Tech only)
            </p>
          </div>
          <Badge variant="info" className="flex items-center gap-1">
            <Shield className="w-4 h-4" />
            Admin Access
          </Badge>
        </div>
      </div>

      {/* Review Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Proposals</p>
              <p className="text-2xl font-bold">{statistics.totalProposals}</p>
            </div>
            <FileText className="h-8 w-8 text-blue-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">In Review</p>
              <p className="text-2xl font-bold">{statistics.inReview}</p>
            </div>
            <Clock className="h-8 w-8 text-orange-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Approved</p>
              <p className="text-2xl font-bold text-green-600">{statistics.approved}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Pending Assignments</p>
              <p className="text-2xl font-bold">{statistics.pendingAssignments}</p>
            </div>
            <Users className="h-8 w-8 text-purple-500 opacity-50" />
          </div>
        </Card>
      </div>

      {/* Bulk Actions */}
      {selectedProposals.size > 0 && (
        <Card className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              <span className="font-medium">
                {selectedProposals.size} proposal(s) selected
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedProposals(new Set())}
            >
              <X className="h-4 w-4" />
              Clear Selection
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowBulkAssign(!showBulkAssign)}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Bulk Assign Reviewer
            </Button>
            <Button
              variant="success"
              size="sm"
              onClick={handleBulkApprove}
              disabled={bulkApproveMutation.isPending}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {bulkApproveMutation.isPending ? 'Approving...' : 'Bulk Approve'}
            </Button>
          </div>

          {/* Bulk Assign Form */}
          {showBulkAssign && (
            <div className="mt-4 p-4 bg-white dark:bg-gray-800 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
              <h4 className="font-medium">Assign Reviewer to Selected Proposals</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Reviewer</label>
                  <Select
                    value={bulkReviewer}
                    onChange={(e) => setBulkReviewer(e.target.value)}
                  >
                    <option value="">Select reviewer...</option>
                    {availableReviewersForBulk?.map((reviewer: AvailableReviewer) => (
                      <option key={reviewer.id} value={reviewer.id}>
                        {reviewer.full_name || reviewer.username}
                        {reviewer.expertise.length > 0 ? ` (${reviewer.expertise.join(', ')})` : ''}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Review Type</label>
                  <Select
                    value={bulkReviewType}
                    onChange={(e) => setBulkReviewType(e.target.value as ReviewType)}
                  >
                    <option value="full">Full Review</option>
                    <option value="quick">Quick Review</option>
                    <option value="technical">Technical Review</option>
                    <option value="compliance">Compliance Review</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Deadline (Optional)</label>
                  <Input
                    type="date"
                    value={bulkDeadline}
                    onChange={(e) => setBulkDeadline(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleBulkAssign}
                  disabled={bulkAssignMutation.isPending || !bulkReviewer}
                >
                  {bulkAssignMutation.isPending ? 'Assigning...' : 'Assign to All Selected'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBulkAssign(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Enhanced Filters */}
      <Card className="mb-6 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </h3>
          {(filters.status !== 'all' ||
            filters.clientName ||
            filters.dateRange ||
            filters.reviewer ||
            filters.reviewType !== 'all' ||
            filters.assignmentStatus !== 'all' ||
            filters.revisionStatus !== 'all') && (
            <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              clientName: '',
              dateRange: undefined,
              reviewer: '',
              reviewType: 'all',
              assignmentStatus: 'all',
              revisionStatus: 'all',
            })}>
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Status</label>
            <Select
              value={filters.status || 'all'}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value as ReviewFilters['status'] })
              }
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="submitted">Submitted</option>
              <option value="archived">Archived</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Client Name</label>
            <Select
              value={filters.clientName || ''}
              onChange={(e) => setFilters({ ...filters, clientName: e.target.value })}
            >
              <option value="">All Clients</option>
              {uniqueClients.map((client) => (
                <option key={client} value={client}>
                  {client}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Reviewer</label>
            <Select
              value={filters.reviewer || ''}
              onChange={(e) => setFilters({ ...filters, reviewer: e.target.value })}
            >
              <option value="">All Reviewers</option>
              {uniqueReviewers.map((reviewer) => (
                <option key={reviewer} value={reviewer}>
                  {reviewer}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Review Type</label>
            <Select
              value={filters.reviewType || 'all'}
              onChange={(e) => setFilters({ ...filters, reviewType: e.target.value })}
            >
              <option value="all">All Types</option>
              <option value="full">Full Review</option>
              <option value="quick">Quick Review</option>
              <option value="technical">Technical Review</option>
              <option value="compliance">Compliance Review</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Assignment Status</label>
            <Select
              value={filters.assignmentStatus || 'all'}
              onChange={(e) => setFilters({ ...filters, assignmentStatus: e.target.value })}
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Revision Status</label>
            <Select
              value={filters.revisionStatus || 'all'}
              onChange={(e) => setFilters({ ...filters, revisionStatus: e.target.value })}
            >
              <option value="all">All Statuses</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-theme-text mb-1">Date Range</label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="date"
                value={filters.dateRange?.start || ''}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    dateRange: { ...filters.dateRange, start: e.target.value },
                  })
                }
                className="w-full"
              />
              <Input
                type="date"
                value={filters.dateRange?.end || ''}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    dateRange: { ...filters.dateRange, end: e.target.value },
                  })
                }
                className="w-full"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Full-Width Proposal List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Proposals Awaiting Review</h2>
          <Badge variant="default">
            {reports ? reports.filter((r: Report) => {
              const metadata = r.metadata || {};
              const isRfpProposal =
                r.report_type === 'rfp_proposal' ||
                (r.report_type === 'custom' &&
                 (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));
              const reviewStatus = r.status === 'completed' ? 'draft' : r.status;
              return isRfpProposal && (reviewStatus === 'draft' || reviewStatus === 'in_review');
            }).length : 0} proposal{reports && reports.filter((r: Report) => {
              const metadata = r.metadata || {};
              const isRfpProposal =
                r.report_type === 'rfp_proposal' ||
                (r.report_type === 'custom' &&
                 (metadata.report_type_override === 'rfp_proposal' || metadata.rfp_artifact_id));
              const reviewStatus = r.status === 'completed' ? 'draft' : r.status;
              return isRfpProposal && (reviewStatus === 'draft' || reviewStatus === 'in_review');
            }).length !== 1 ? 's' : ''}
          </Badge>
        </div>

        <AggregatedReviewList
          filters={filters}
          onProposalClick={handleProposalClick}
          onStatusChange={handleStatusChange}
          canChangeStatus={canAccessReview}
          isReviewer={isReviewer}
          onClientNamesExtracted={setAvailableClientNames}
          onReviewersExtracted={setAvailableReviewers}
        />
      </div>
    </div>
  );
}
